import * as z from "@-/com.npmjs.zod";

/** `start.level` — see {@link slideAnchorSchema}. */
export const levelStartSlideAnchorSchema = z.object({
  disabled: z.optional(z.boolean()),
  start: z.object({ level: z.number().int().min(0), part: z.optional(z.literal("pronunciation")) }),
});

/** `end.level` — after the last character (and pronunciation slots) of a level. */
export const levelEndSlideAnchorSchema = z.object({
  disabled: z.optional(z.boolean()),
  end: z.object({
    level: z.number().int().min(0),
    part: z.optional(z.enum(["character", "pronunciation"])),
  }),
});

/**
 * The pronunciation card inferred from its owning lesson's YAML.
 * `pinyin` is tone-number form, e.g. `an4`.
 */
const slideAnchorCardSchema = z.object({
  kind: z.literal("pronunciation"),
  pinyin: z.string().min(1),
});

const glyphSlideAnchorTargetSchema = z.object({
  card: z.optional(slideAnchorCardSchema),
  glyph: z.string().min(1),
});

/**
 * Derived slide positions from nested lesson and level files:
 *
 * - before/after.glyph: the owning grapheme lesson. Pronunciation lesson YAML supplies its
 *   exact card reading; an explicit reading slot wins over a combined character lesson.
 * - start/end.level: the owning level boundary. Pronunciation boundaries identify their part
 *   so an intro follows the character lessons and a character outro precedes the P-level.
 *
 * Glyph anchors resolve against the active script. Slides sharing an anchor retain their
 * authored order. Disabled slides keep their identities reserved but are not bundled.
 */
export const slideAnchorSchema = z.union([
  z.object({
    after: glyphSlideAnchorTargetSchema,
    disabled: z.optional(z.boolean()),
  }),
  z.object({
    before: glyphSlideAnchorTargetSchema,
    disabled: z.optional(z.boolean()),
  }),
  levelStartSlideAnchorSchema,
  levelEndSlideAnchorSchema,
]);

export type SlideAnchor = z.infer<typeof slideAnchorSchema>;

/**
 * A slide's manifest entry: where it attaches, and its position among the slide cards and quizzes
 * sharing a sequence position.
 *
 * `order` is DERIVED at build time (never authored): one running ordinal across every slide card
 * and quiz, in authored order (level and lesson folder order across files; within one anchor, its
 * slide card and then its quizzes). Two anchors can land on the same sequence position (`after` one
 * character and `before` the next); the ordinal keeps them in authored order.
 */
export type SlideManifestEntry = {
  anchor: SlideAnchor;
  /** Optional authored icon export name; consumers supply a fallback. */
  icon?: string;
  /**
   * Unchanging persisted identity (`user_slide.key`). Always a UUID — never edit an authored key.
   */
  key: string;
  order: number;
  /** URL identity carried in routes and the MDX component registry. May change without orphaning state. */
  slug: string;
  /**
   * The compiled `.mdx` module's path relative to this package (`@grapheme-slides/…`), so Metro can
   * map its compiled module to the slug.
   */
  sourcePath: string;
};

/** `one`: exactly one option is correct. `all`: select every correct option (one or more). */
export type QuizSelect = "all" | "one";

/** One answer option, as authored by `<QuizOption glyph="…" correct />`. */
export type QuizOptionSpec = { correct: boolean; glyph: string };

/**
 * A quiz's manifest entry: where it attaches (after the slide card sharing its anchor), and its
 * answer key, read from its `<Quiz>` at build time so grading never inspects rendered children.
 */
export type QuizManifestEntry = {
  anchor: SlideAnchor;
  /**
   * Unchanging persisted identity (`user_quiz_card.key`). Always a UUID — never edit an authored
   * key. Shares its namespace with slide keys.
   */
  key: string;
  /** In authored order; the answer options a showing shuffles. */
  options: QuizOptionSpec[];
  /** See {@link SlideManifestEntry.order}: shared with slide cards. */
  order: number;
  select: QuizSelect;
  /** URL identity carried in routes. Shares its namespace with slide slugs. */
  slug: string;
  /** The compiled `.mdx` module's path relative to this package (`@grapheme-slides/…`). */
  sourcePath: string;
  /** The prompt as plain text, for surfaces that list the quiz (stats, deck lists). */
  title: string;
};

export function isAfterAnchor(
  anchor: SlideAnchor,
): anchor is Extract<SlideAnchor, { after: unknown }> {
  return "after" in anchor;
}

export function isBeforeAnchor(
  anchor: SlideAnchor,
): anchor is Extract<SlideAnchor, { before: unknown }> {
  return "before" in anchor;
}

export function isStartAnchor(
  anchor: SlideAnchor,
): anchor is Extract<SlideAnchor, { start: unknown }> {
  return "start" in anchor;
}

export function isEndAnchor(anchor: SlideAnchor): anchor is Extract<SlideAnchor, { end: unknown }> {
  return "end" in anchor;
}
